/**
 * Converts level (%) to kg using silo geometry (cylinder + lower cone).
 * Level is computed as: (filled volume) / (total volume) — "área da superfície dentro do silo
 * dividida pela área total", using cylinder and lower cone (upper cone not considered).
 *
 * Silo dimensions come from the "Silo dimensions" curve in silo_profiles/silo_last_profiles_by_silo/{id}/
 * Curve points: { x: number[], y: number[] } defining the 2D cross-section (x = radius, y = height).
 */

/**
 * Parse "Silo dimensions" curve points to extract radius, cone height and total height.
 * Expects points defining: vertical walls (same x, varying y) and cone bottom (y from 0 to cone top).
 * @param {{ x: number[], y: number[] }} points - curve.points from Silo dimensions
 * @returns {{ radius: number, coneHeight: number, cylinderHeight: number, totalHeight: number } | null}
 */
export function parseSiloDimensionsFromCurve(points) {
  if (!points || !Array.isArray(points.x) || !Array.isArray(points.y) || points.x.length !== points.y.length) {
    return null;
  }
  const x = points.x.map(Number).filter(Number.isFinite);
  const y = points.y.map(Number).filter(Number.isFinite);
  if (x.length < 2 || y.length < 2) return null;

  const minY = Math.min(...y);
  const maxY = Math.max(...y);
  const totalHeight = maxY - minY;
  if (totalHeight <= 0) return null;

  // Radius = max |x| on the vertical segments (repeated x with different y)
  const radius = Math.max(...x.map(xi => Math.abs(xi)));
  if (radius <= 0) return null;

  // Cone: bottom of silo. Find the y where the "vertical" part starts (same as SiloCard: cone top = min y of vertical wall).
  // Points with |x| == radius and y > minY: the smallest such y is the top of the cone.
  const verticalYs = y.filter((yi, i) => Math.abs(x[i]) >= radius * 0.99);
  const coneTopY = verticalYs.length > 0 ? Math.min(...verticalYs) : minY + 1;
  const coneHeight = coneTopY - minY;
  const cylinderHeight = totalHeight - coneHeight;

  if (coneHeight < 0 || cylinderHeight < 0) return null;

  return {
    radius,
    coneHeight,
    cylinderHeight,
    totalHeight
  };
}

/**
 * Compute total volume (m³) of silo: cylinder + cone (lower cone only).
 * @param {{ radius: number, coneHeight: number, cylinderHeight: number }} dims - from parseSiloDimensionsFromCurve
 * @returns {number} volume in m³
 */
export function siloTotalVolumeM3(dims) {
  if (!dims || dims.radius <= 0) return 0;
  const { radius, coneHeight, cylinderHeight } = dims;
  const R = radius;
  const Vcone = (1 / 3) * Math.PI * R * R * coneHeight;
  const Vcyl = Math.PI * R * R * cylinderHeight;
  return Vcone + Vcyl;
}

/**
 * Volume (m³) corresponding to a given level (%).
 * Level is interpreted as volume ratio: level% = 100 * V_filled / V_total.
 * @param {number} levelPercent - 0..100
 * @param {{ radius: number, coneHeight: number, cylinderHeight: number, totalHeight: number }} dims
 * @returns {number} filled volume in m³
 */
export function volumeAtLevelPercent(levelPercent, dims) {
  if (!dims || levelPercent <= 0) return 0;
  if (levelPercent >= 100) return siloTotalVolumeM3(dims);
  const Vtotal = siloTotalVolumeM3(dims);
  return (levelPercent / 100) * Vtotal;
}

/**
 * Convert volume-based level (%) into fill geometry for rendering.
 * For a lower cone, V(h) = V_cone * (h / h_cone)^3, so height is not linear with level.
 *
 * @param {number} levelPercent - 0..100 (volume ratio)
 * @param {{ radius: number, coneHeight: number, cylinderHeight: number, totalHeight: number } | null} dims
 * @returns {{
 *   fillHeightM: number,
 *   coneFillRatio: number,
 *   cylinderFillRatio: number,
 *   inConeOnly: boolean,
 * }}
 */
export function fillGeometryFromLevelPercent(levelPercent, dims) {
  const empty = {
    fillHeightM: 0,
    coneFillRatio: 0,
    cylinderFillRatio: 0,
    inConeOnly: true,
  };

  const level = Number(levelPercent) || 0;
  if (level <= 0) return empty;

  if (!dims || dims.radius <= 0) {
    const ratio = Math.min(1, Math.max(0, level / 100));
    return {
      fillHeightM: 0,
      coneFillRatio: 0,
      cylinderFillRatio: ratio,
      inConeOnly: false,
    };
  }

  const { radius: R, coneHeight, cylinderHeight, totalHeight } = dims;
  const Vcone = coneHeight > 0 ? (1 / 3) * Math.PI * R * R * coneHeight : 0;
  const Vcyl = cylinderHeight > 0 ? Math.PI * R * R * cylinderHeight : 0;
  const Vtotal = Vcone + Vcyl;

  if (Vtotal <= 0) return empty;

  if (level >= 100) {
    return {
      fillHeightM: totalHeight,
      coneFillRatio: coneHeight > 0 ? 1 : 0,
      cylinderFillRatio: cylinderHeight > 0 ? 1 : 0,
      inConeOnly: false,
    };
  }

  const Vfilled = (level / 100) * Vtotal;

  if (Vcone > 0 && Vfilled <= Vcone) {
    const coneFillRatio = Math.cbrt(Vfilled / Vcone);
    return {
      fillHeightM: coneFillRatio * coneHeight,
      coneFillRatio,
      cylinderFillRatio: 0,
      inConeOnly: true,
    };
  }

  const cylinderFillRatio =
    Vcyl > 0 ? Math.min(1, Math.max(0, (Vfilled - Vcone) / Vcyl)) : 0;

  return {
    fillHeightM: coneHeight + cylinderFillRatio * cylinderHeight,
    coneFillRatio: coneHeight > 0 ? 1 : 0,
    cylinderFillRatio,
    inConeOnly: false,
  };
}

/**
 * Kg corresponding to a level delta (e.g. +19% entrada).
 * deltaKg = (deltaLevelPercent/100) * V_total * density.
 * @param {number} deltaLevelPercent - can be positive (entrada) or negative (saída)
 * @param {Object} dims - from parseSiloDimensionsFromCurve
 * @param {number} densityKgM3 - density in kg/m³ (e.g. 750 for soja)
 * @returns {number} kg (always >= 0; use sign of deltaLevelPercent for entrada vs saída)
 */
export function kgFromLevelDelta(deltaLevelPercent, dims, densityKgM3) {
  if (!dims || !Number.isFinite(deltaLevelPercent)) return 0;
  const density = Number(densityKgM3) || 750;
  const Vtotal = siloTotalVolumeM3(dims);
  return Math.abs(deltaLevelPercent) / 100 * Vtotal * density;
}

/**
 * Get dimensions from a profile's curves (e.g. last profile by silo).
 * @param {Array<{ name: string, points: { x: number[], y: number[] } }>} curves
 * @returns {Object | null} parseSiloDimensionsFromCurve result or null
 */
/**
 * Normaliza curves quando a API retorna objeto único (silo_dimensions_only=true).
 */
export function normalizeSiloProfileCurves(curves) {
  if (!curves) return [];
  if (Array.isArray(curves)) return curves;
  if (typeof curves === 'object' && curves.points) return [curves];
  return [];
}

/**
 * Normaliza perfil retornado por silo_last_profiles_by_silo.
 */
export function normalizeSiloLastProfile(profile) {
  if (!profile || typeof profile !== 'object') return profile;

  return {
    ...profile,
    food: profile.food ?? profile.feed,
    curves: normalizeSiloProfileCurves(profile.curves),
    last_updated: profile.last_updated ?? profile.datetime_fetched,
  };
}

export function findSiloDimensionsCurve(curves) {
  const list = normalizeSiloProfileCurves(curves);
  return list.find(c => c && (c.name === 'Silo dimensions' || c.name === 'silo_dimensions')) || null;
}

export function hasSiloDimensionsCurve(curves) {
  const curve = findSiloDimensionsCurve(curves);
  return !!(curve && curve.points);
}

export function mergeProfileWithDimensionsCurve(profile, dimensionsCurve) {
  if (!profile) return profile;
  if (!dimensionsCurve) return profile;

  const curves = normalizeSiloProfileCurves(profile.curves);
  const index = curves.findIndex(
    c => c && (c.name === 'Silo dimensions' || c.name === 'silo_dimensions')
  );

  if (index >= 0) {
    curves[index] = dimensionsCurve;
  } else {
    curves.unshift(dimensionsCurve);
  }

  return { ...profile, curves };
}

export function getSiloDimensionsFromCurves(curves) {
  const curve = findSiloDimensionsCurve(curves);
  if (!curve || !curve.points) return null;
  return parseSiloDimensionsFromCurve(curve.points);
}

/** Dims for fundo chato: volume is proportional to height in the cylinder only. */
export function getFlatBottomDimensionsFromCurves(curves) {
  const dims = getSiloDimensionsFromCurves(curves);
  if (!dims) return null;
  return {
    ...dims,
    coneHeight: 0,
    cylinderHeight: dims.totalHeight,
  };
}

/**
 * Capacidade volumétrica total (m³): curva "Silo dimensions" ou estimativa por nível/volume do perfil.
 */
export function estimateTotalCapacityVolumeM3({ curves, level, volume } = {}) {
  const dims = getSiloDimensionsFromCurves(curves);
  if (dims) {
    const fromGeometry = siloTotalVolumeM3(dims);
    if (Number.isFinite(fromGeometry) && fromGeometry > 0) return fromGeometry;
  }

  const lvl = Number(level) || 0;
  const vol = Number(volume) || 0;
  if (lvl > 0.1 && vol > 0) {
    return vol / (lvl / 100);
  }

  return null;
}

/** Same threshold as SiloCard displayVolume / getSiloDisplayWeightAndBags. */
export const EMPTY_SILO_LEVEL_THRESHOLD = 0.1;

/**
 * Capacidade restante em toneladas para um perfil/ponto do histórico.
 */
export function remainingCapacityTonsFromProfile({ curves, level, volume, density, weight, useLevel = false, informedMaxWeightKg } = {}) {
  const lvl = Number(level) || 0;
  const isEmpty = lvl < EMPTY_SILO_LEVEL_THRESHOLD;

  const informedMax = Number(informedMaxWeightKg);
  if (Number.isFinite(informedMax) && informedMax > 0) {
    const totalTons = informedMax / 1000;
    const currentTons = isEmpty
      ? 0
      : (useLevel ? (lvl / 100) * totalTons : (Number(weight) || 0) / 1000);
    return Math.max(0, totalTons - currentTons);
  }

  const dims = getSiloDimensionsFromCurves(curves);
  let total;
  let currentVol;

  if (useLevel && dims) {
    total = siloTotalVolumeM3(dims);
    currentVol = isEmpty ? 0 : volumeAtLevelPercent(lvl, dims);
  } else {
    total = estimateTotalCapacityVolumeM3({ curves, level, volume });
    currentVol = isEmpty ? 0 : (Number(volume) > 0 ? Number(volume) : 0);
  }

  if (total == null) return null;

  const remainingVol = Math.max(0, total - currentVol);

  let effectiveDensity = Number(density);
  if (!Number.isFinite(effectiveDensity) || effectiveDensity <= 0) {
    const w = Number(weight) || 0;
    const v = Number(volume) || 0;
    if (v > 0 && w > 0) effectiveDensity = w / v;
  }
  if (!Number.isFinite(effectiveDensity) || effectiveDensity <= 0) return null;

  return (remainingVol * effectiveDensity) / 1000;
}

export function shouldUseLevelFromSettings(siloSettings) {
  const useLevel = siloSettings?.[0]?.settings?.use_level;
  return useLevel === true || useLevel === 'true' || useLevel === 1 || useLevel === '1';
}

/**
 * Derives volume, weight and bags from level (%) and silo geometry when use_level is enabled.
 */
export function profileMetricsFromLevel({ level, curves, density, sacaWeight = 60 } = {}) {
  const dims = getSiloDimensionsFromCurves(curves);
  if (!dims) return null;

  const lvl = Number(level) || 0;
  if (lvl <= 0) {
    return { volume: 0, weight: 0, bags: 0 };
  }

  const volume = volumeAtLevelPercent(lvl, dims);
  const effectiveDensity = Number(density) || 0;
  if (effectiveDensity <= 0) {
    return { volume, weight: null, bags: null };
  }

  const weight = volume * effectiveDensity;
  const safeSacaWeight = Number(sacaWeight) || 60;
  const bags = safeSacaWeight > 0 ? weight / safeSacaWeight : 0;

  return { volume, weight, bags };
}
